import React from "react";
import {
  Center,
  ComponentMultiStyleConfig,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { BsThreeDots } from "react-icons/bs";
import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import { IconType } from "react-icons";

export type CMExtendedMenuProps<T extends string | number> = {
  size?: "xs" | "sm" | "md" | "lg";

  /**
   * ボタンのアイコン
   */
  buttonIcon?: IconType;

  /**
   * メニューの項目リスト
   */
  menuItems?: Item<T>[];

  /**
   * メニュー項目クリック時のイベント
   */
  onClick?: (key: T) => void;

  menuZIndex?: number;
} & ComponentStyleProps;

type Item<T extends string | number> = {
  key: T;
  icon?: IconType;
  label?: string;
  labelColor?: string;
};

/**
 * 拡張メニューを表示するコンポーネント
 */
export const CMExtendedMenu = <T extends string | number>({
  size = "md",
  buttonIcon = BsThreeDots,
  menuItems,
  onClick,

  menuZIndex,

  sx,
  ...rest
}: CMExtendedMenuProps<T>) => {
  const styles = useMultiStyleConfig("CMExtendedMenuStyles");

  return (
    <Center sx={{ ...styles.container, ...sx }} {...rest}>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={buttonIcon} sx={styles.buttonIcon} />}
          size={size}
        />
        <MenuList sx={styles.menuList} zIndex={menuZIndex}>
          {menuItems?.map((menuItem, index: number) => {
            return (
              <MenuItem
                key={index}
                icon={
                  menuItem.icon && (
                    <Icon as={menuItem.icon} sx={styles.menuIcon} />
                  )
                }
                iconSpacing={"10px"}
                color={menuItem.labelColor}
                onClick={() => onClick?.(menuItem.key)}
                sx={styles.menuItem}
              >
                <Text sx={styles.menuLabel}>{menuItem.label}</Text>
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Center>
  );
};

export const CMExtendedMenuStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "buttonIcon",
    "menuList",
    "menuItem",
    "menuLabel",
    "menuIcon",
  ],
  baseStyle: {
    container: {
      w: "32px",
      h: "32px",
    },
    buttonIcon: {
      w: "80%",
      h: "80%",
      color: "gray.700",
    },
    menuList: {},
    menuItem: {},
    menuLabel: {
      fontColor: "gray.900",
      fontSize: "md",
      fontWeight: "normal",
    },
    menuIcon: {
      display: "flex",
      fontSize: "20px",
      alignItems: "center",
    },
  },
};
